import { Box, ClickAwayListener, Typography, Popper, IconButton, Divider } from '@mui/material';
import { NoteIcon } from '../../../../icons';
import { useRef, useState } from 'react';
import { RoundedPaper } from '../../../../components';
import { useTranslation } from 'react-i18next';

interface Note {
  id: string;
  name: string;
}

interface NoteCellProps {
  note?: Note | string;
}

export const NoteCell: React.FC<NoteCellProps> = ({ note }) => {
  const hasNote = !!note;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelayedClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      handleClose();
    }, 50);
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const getNoteContent = (note?: Note | string): string => {
    if (!note) return '';
    if (typeof note === 'string') return note;
    return note.name || '';
  };

  const renderContent = (content?: Note | string) => {
    const textContent = getNoteContent(content);
    return (
      <>
        <Box sx={{ p: 2 }}>
          <Typography fontWeight="bold" color="primary.main">
            {t('common:NOTE')}
          </Typography>
        </Box>

        <Divider />

        <Typography className="text-sm p-4 w-full h-full max-h-[250px] overflow-scroll">
          {textContent
            ? textContent.split('\n').map((line, index) => renderContentLine(line, index))
            : ''}
        </Typography>
      </>
    );
  };

  const renderContentLine = (line: string, index: number) => {
    return (
      <Box key={index} component="span" minHeight="16px">
        {line}
      </Box>
    );
  };

  return (
    <>
      <IconButton
        onMouseOver={handleOpen}
        onMouseLeave={handleDelayedClose}
        disabled={!hasNote}
        sx={{ padding: 0 }}
      >
        <NoteIcon sx={{ opacity: !hasNote ? '0.3' : '1' }} />
      </IconButton>

      <Popper
        id="note-overlay"
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-end"
        modifiers={[
          {
            fn: (popper) => {
              const y = popper.state.rects.reference.y;
              if (y >= window.innerHeight - 150 || y <= 150) {
                handleClose();
              }
            },
            phase: 'main',
            enabled: true,
            name: 'closeOutOffView'
          }
        ]}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleClose}
        className="z-50"
      >
        <ClickAwayListener onClickAway={handleClose}>
          <RoundedPaper className="flex bg-white w-[300px] min-h-[50px] border-none shadow-500">
            <Box flex="1">{renderContent(note)}</Box>
          </RoundedPaper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
